import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { 
  DocumentTextIcon, 
  PlusIcon, 
  EyeIcon, 
  PencilIcon, 
  PrinterIcon,
  CheckCircleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  InformationCircleIcon,
  BookOpenIcon,
  DocumentArrowDownIcon
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { DocumentSidebar } from "../../../../components/dashboard/documents/DocumentSidebar";
import { getHseDocuments, printDocument, printMultipleDocuments } from "../../../../api/hse/documents";

const DOCUMENT_LIST_STATE_KEY = "promang:hse-documents:list";

const readDocumentListState = () => {
  try {
    const raw = window.sessionStorage.getItem(DOCUMENT_LIST_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

/**
 * HSE/KS Documents List - Controlled documents library
 */
export const HseKsDocumentsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const initialListStateRef = useRef(readDocumentListState());
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => initialListStateRef.current.searchQuery || "");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(() => initialListStateRef.current.searchQuery || "");
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const infoTooltipRef = useRef(null);
  const [selectedDiscipline, setSelectedDiscipline] = useState(
    () => initialListStateRef.current.selectedDiscipline ?? null
  );
  const [selectedCategory, setSelectedCategory] = useState(
    () => initialListStateRef.current.selectedCategory ?? null
  );
  const [selectedChapter, setSelectedChapter] = useState(
    () => initialListStateRef.current.selectedChapter ?? null
  );
  const [printingDocumentId, setPrintingDocumentId] = useState(null);
  const [selectedDocumentsForPrint, setSelectedDocumentsForPrint] = useState(new Set());
  const [isPrintingBook, setIsPrintingBook] = useState(false);
  
  // All hooks must be called before any conditional logic
  const canCreate = useUserHasAccess({ role: "hse_ks_documents", action: "create" });
  const canApproveRole = useUserHasAccess({ role: "hse_ks_documents_approve", action: "approve" });
  
  // Conditional logic after hooks
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  const canApprove = isAdmin || canApproveRole;
  const canManage = isAdmin || canApprove;

  const [activeFilter, setActiveFilter] = useState(() => {
    const path = location.pathname;
    if (path.includes("/policies")) return "policies";
    if (path.includes("/procedures")) return "procedures";
    if (path.includes("/instructions")) return "instructions";
    if (path.includes("/forms")) return "forms";
    return "all";
  });

  const filters = [
    { id: "all", label: "Alle", path: "/dashboard/hse-ks/documents" },
    { id: "policies", label: "Policyer", path: "/dashboard/hse-ks/documents/policies" },
    { id: "procedures", label: "Prosedyrer", path: "/dashboard/hse-ks/documents/procedures" },
    { id: "instructions", label: "Instrukser", path: "/dashboard/hse-ks/documents/instructions" },
    { id: "forms", label: "Skjemaer", path: "/dashboard/hse-ks/documents/forms" },
    { id: "guidance", label: "Veiledninger", path: "/dashboard/hse-ks/documents/guidance" },
    { id: "record", label: "Registreringer", path: "/dashboard/hse-ks/documents/record" },
    { id: "template", label: "Maler", path: "/dashboard/hse-ks/documents/template" },
  ];

  useEffect(() => {
    fetchDocuments();
  }, [activeFilter, debouncedSearchQuery, selectedDiscipline, selectedCategory, selectedChapter]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        DOCUMENT_LIST_STATE_KEY,
        JSON.stringify({
          searchQuery,
          selectedDiscipline,
          selectedCategory,
          selectedChapter,
        })
      );
    } catch {
      // Best-effort UI state only.
    }
  }, [searchQuery, selectedDiscipline, selectedCategory, selectedChapter]);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedChapter(null);
    // Filtrer dokumenter basert på kategori
  };

  const handleChapterSelect = (categoryId, chapterNumber) => {
    setSelectedCategory(categoryId);
    setSelectedChapter(chapterNumber);
    // Filtrer dokumenter basert på kategori og kapittel
    // Dokumenter vil automatisk filtreres i fetchDocuments når selectedChapter endres
  };

  const handleAddDocument = (categoryId, chapterNumber) => {
    // Navigate to create document page with pre-filled discipline, category and chapter
    const params = new URLSearchParams();
    if (selectedDiscipline) params.append('discipline', selectedDiscipline);
    if (categoryId) params.append('category', categoryId);
    if (chapterNumber) params.append('chapter', chapterNumber);
    navigate(`/dashboard/hse-ks/documents/new?${params.toString()}`);
  };

  const handleAddChapter = (categoryId, parentChapterNumber) => {
    // This is handled by DocumentSidebar component now
    // The sidebar will show a dialog and handle the creation
    console.log("Add chapter:", { categoryId, parentChapterNumber });
  };

  const handleEditChapter = async (categoryId, chapterNumber, newData) => {
    try {
      // Find chapter ID from disciplines data
      // This will be handled by DocumentSidebar component
      console.log("Edit chapter:", { categoryId, chapterNumber, newData });
      toast.success(t("Kapittel oppdatert"));
      // Refresh documents after edit
      fetchDocuments();
    } catch (err) {
      console.error("Error editing chapter:", err);
      toast.error(t("Kunne ikke oppdatere kapittel"));
    }
  };

  const handleDeleteChapter = async (categoryId, chapterNumber) => {
    try {
      // This will be handled by DocumentSidebar component
      console.log("Delete chapter:", { categoryId, chapterNumber });
      toast.success(t("Kapittel slettet"));
      // Clear selection if deleted chapter was selected
      if (selectedChapter === chapterNumber) {
        setSelectedChapter(null);
      }
      // Refresh documents after delete
      fetchDocuments();
    } catch (err) {
      console.error("Error deleting chapter:", err);
      toast.error(t("Kunne ikke slette kapittel"));
    }
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (infoTooltipRef.current && !infoTooltipRef.current.contains(event.target)) {
        setShowInfoTooltip(false);
      }
    };

    if (showInfoTooltip) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showInfoTooltip]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(false);
      
      // Build filter parameters
      const filters = {};
      
      if (activeFilter !== "all") {
        // Map filter IDs to document types
        const documentTypeMap = {
          policies: "policy",
          procedures: "procedure",
          instructions: "instruction",
          forms: "form",
          guidance: "guidance",
          record: "record",
          template: "template"
        };
        filters.document_type = documentTypeMap[activeFilter] || activeFilter;
      }
      
      if (debouncedSearchQuery) {
        filters.search = debouncedSearchQuery;
      }
      
      if (selectedCategory != null && selectedCategory !== "") {
        filters.category = selectedCategory;
      }
      
      if (selectedChapter != null && selectedChapter !== "") {
        // API forventer kapittelnummer som streng (samme som Chapter.number i databasen)
        filters.chapter = String(selectedChapter);
      }
      
      // Only add discipline_id filter if a specific discipline is selected (not "Alle")
      if (selectedDiscipline && selectedDiscipline !== "" && selectedDiscipline !== null) {
        filters.discipline_id = selectedDiscipline;
      }
      
      const response = await getHseDocuments(filters);
      
      // Check for errors in response
      if (response.errors && response.errors.length > 0) {
        setError(true);
        const errorMessage = response.errors[0] || t("Kunne ikke laste dokumenter");
        toast.error(errorMessage);
        setDocuments([]);
        return;
      }
      
      // Validate response structure
      if (!response || typeof response !== 'object') {
        console.error("Invalid response structure:", response);
        setError(true);
        const errorMsg = t("Ugyldig respons fra server. Vennligst prøv igjen eller kontakt support hvis problemet vedvarer.");
        toast.error(errorMsg);
        setDocuments([]);
        return;
      }
      
      // Set documents (empty array if documents is undefined/null)
      setDocuments(response.documents || []);
    } catch (err) {
      console.error("Error fetching documents:", err);
      setError(true);
      
      // Extract detailed error information
      let errorMessage = t("Kunne ikke laste dokumenter");
      
      if (err.response) {
        // Server responded with error status
        const status = err.response.status;
        const data = err.response.data;
        
        if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          errorMessage = data.errors[0];
        } else if (data?.error) {
          errorMessage = data.error;
        } else {
          // Provide status-specific messages
          switch (status) {
            case 400:
              errorMessage = t("Ugyldig forespørsel. Vennligst sjekk at alle filtre er korrekte.");
              break;
            case 401:
              errorMessage = t("Du er ikke autentisert. Vennligst logg inn på nytt.");
              break;
            case 403:
              errorMessage = t("Du har ikke tilgang til å se disse dokumentene.");
              break;
            case 404:
              errorMessage = t("Ressursen ble ikke funnet. Vennligst prøv igjen.");
              break;
            case 500:
              errorMessage = t("Serverfeil. Vennligst prøv igjen senere eller kontakt support hvis problemet vedvarer.");
              break;
            default:
              errorMessage = t("En feil oppstod ved lasting av dokumenter. Status: {status}", { status });
          }
        }
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = t("Kunne ikke koble til serveren. Vennligst sjekk internettforbindelsen og prøv igjen.");
      } else {
        // Error in request setup
        errorMessage = err.message || errorMessage;
      }
      
      toast.error(errorMessage);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (documentId) => {
    // Prevent multiple clicks
    if (printingDocumentId === documentId) {
      return;
    }
    
    try {
      // Only print approved documents
      const doc = documents.find(d => d.id === documentId);
      if (!doc) {
        toast.error(t("Dokument ikke funnet"));
        return;
      }
      
      // Check if document has approved version
      // has_approved_version can be undefined if not set by API, so we check status as fallback
      // Only documents with approved version can be printed, even for admin
      const hasApprovedVersion = doc.has_approved_version === true || doc.status === 'approved';
      if (!hasApprovedVersion) {
        toast.error(t("Kun godkjente dokumenter kan skrives ut"));
        return;
      }
      
      setPrintingDocumentId(documentId);
      const response = await printDocument(documentId, null, 'document');
      if (response.pdf_url) {
        window.open(response.pdf_url, '_blank');
        toast.success(t("Dokument skrives ut..."));
      } else {
        toast.error(t("Kunne ikke generere PDF"));
      }
    } catch (err) {
      console.error("Error printing document:", err);
      const errorMessage = err.response?.data?.errors?.[0] || err.message || t("Kunne ikke skrive ut dokument");
      toast.error(errorMessage);
    } finally {
      setPrintingDocumentId(null);
    }
  };
  
  const handlePrintMultiple = async (documentIds = null) => {
    try {
      setIsPrintingBook(true);
      
      // If no documentIds provided, use selected documents, or all approved documents if none selected
      let docsToPrint = [];
      
      // Helper function to check if document has approved version
      const hasApprovedVersion = (doc) => {
        return doc.has_approved_version === true || doc.status === 'approved';
      };
      
      if (documentIds && documentIds.length > 0) {
        // Use provided document IDs
        docsToPrint = documents.filter(doc => documentIds.includes(doc.id));
      } else if (selectedDocumentsForPrint.size > 0) {
        // Use selected documents
        docsToPrint = documents.filter(doc => selectedDocumentsForPrint.has(doc.id));
      } else {
        // Use all approved documents (only approved, no admin exception)
        docsToPrint = documents.filter(doc => hasApprovedVersion(doc));
      }
      
      // Filter to only approved documents (no admin exception)
      const approvedDocs = docsToPrint.filter(doc => hasApprovedVersion(doc));
      
      if (approvedDocs.length === 0) {
        toast.error(t("Ingen godkjente dokumenter å skrive ut"));
        return;
      }
      
      const response = await printMultipleDocuments(
        approvedDocs.map(d => d.id),
        'book'
      );
      if (response.pdf_url) {
        window.open(response.pdf_url, '_blank');
        toast.success(t("Dokumentbok skrives ut..."));
        // Clear selection after successful print
        setSelectedDocumentsForPrint(new Set());
      } else {
        toast.error(t("Kunne ikke generere PDF"));
      }
    } catch (err) {
      console.error("Error printing multiple documents:", err);
      const errorMessage = err.response?.data?.errors?.[0] || err.message || t("Kunne ikke skrive ut dokumentbok");
      toast.error(errorMessage);
    } finally {
      setIsPrintingBook(false);
    }
  };
  
  const handleToggleDocumentSelection = (documentId) => {
    const newSelection = new Set(selectedDocumentsForPrint);
    if (newSelection.has(documentId)) {
      newSelection.delete(documentId);
    } else {
      newSelection.add(documentId);
    }
    setSelectedDocumentsForPrint(newSelection);
  };
  
  const handleSelectAllDocuments = () => {
    // Select all documents that can be printed (only approved versions, no admin exception)
    const printableDocs = documents.filter(doc => {
      const hasApprovedVersion = doc.has_approved_version === true || doc.status === 'approved';
      return hasApprovedVersion;
    });
    
    if (selectedDocumentsForPrint.size === printableDocs.length && 
        printableDocs.every(doc => selectedDocumentsForPrint.has(doc.id))) {
      // Deselect all
      setSelectedDocumentsForPrint(new Set());
    } else {
      // Select all printable
      setSelectedDocumentsForPrint(new Set(printableDocs.map(d => d.id)));
    }
  };

  const headerActions = [
    {
      label: "Guide",
      icon: BookOpenIcon,
      path: "/dashboard/hse-ks/documents/guide",
      hidden: false,
      menu: false,
    },
    {
      label: "Maler",
      path: "/dashboard/hse-ks/documents/templates",
      hidden: false,
      menu: false,
    },
    {
      component: null,
      label: isPrintingBook ? t("Genererer dokumentbok...") : t("Skriv ut dokument-bok"),
      action: () => handlePrintMultiple(),
      icon: DocumentArrowDownIcon,
      textColor: "text-gray-700",
      bgColor: "bg-white",
      hidden: false,
      disabled: isPrintingBook || documents.length === 0,
      loading: isPrintingBook,
      menu: false,
    },
    ...(canCreate ? [{
      label: "Nytt dokument",
      icon: PlusIcon,
      path: "/dashboard/hse-ks/documents/new",
      hidden: false,
      menu: false,
    }] : []),
  ];

  return (
    <DashboardHeader
      title="Dokumenter"
      subtitle="Styrende dokumenter med versjonskontroll"
      actions={headerActions}
    >
      <div className="flex h-full mt-8">
                {/* Sidebar */}
                <DocumentSidebar 
                  onDisciplineSelect={setSelectedDiscipline}
                  selectedDisciplineId={selectedDiscipline}
                  onCategorySelect={handleCategorySelect}
                  onChapterSelect={handleChapterSelect}
                  onAddChapter={handleAddChapter}
                  onEditChapter={handleEditChapter}
                  onDeleteChapter={handleDeleteChapter}
                  onAddDocument={handleAddDocument}
                />
        
        {/* Main content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6 max-w-7xl mx-auto">
          {/* Filters */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="border-b border-gray-200 px-4 py-3 sm:px-6">
              <div className="flex items-center justify-between">
                <nav className="flex space-x-4 flex-1" aria-label="Tabs">
                  {filters.map((filter) => (
                    <button
                      key={filter.id}
                      onClick={() => {
                        setActiveFilter(filter.id);
                        navigate(filter.path);
                      }}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeFilter === filter.id
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </nav>
                <div className="relative ml-4" ref={infoTooltipRef}>
                  <button
                    type="button"
                    onClick={() => setShowInfoTooltip(!showInfoTooltip)}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                    aria-label="Informasjon om dokumenttyper"
                  >
                    <InformationCircleIcon className="h-5 w-5" />
                  </button>
                  {showInfoTooltip && (
                    <div className="absolute right-0 top-8 z-10 w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-4">
                      <div className="space-y-3">
                        <div>
                          <h4 className="font-semibold text-sm text-gray-900 mb-1">Policyer</h4>
                          <p className="text-xs text-gray-600">
                            Overordnede retningslinjer og prinsipper som beskriver organisasjonens mål og verdier. 
                            Policyer gir rammeverket for beslutninger og handlinger.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-gray-900 mb-1">Prosedyrer</h4>
                          <p className="text-xs text-gray-600">
                            Detaljerte beskrivelser av hvordan spesifikke oppgaver skal utføres. 
                            Prosedyrer gir trinn-for-trinn instruksjoner for å sikre konsistent utførelse.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-gray-900 mb-1">Instrukser</h4>
                          <p className="text-xs text-gray-600">
                            Konkrete veiledninger for hvordan enkeltoperasjoner eller prosesser skal utføres. 
                            Instrukser er mer detaljerte enn prosedyrer og fokuserer på spesifikke oppgaver.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-gray-900 mb-1">Skjemaer</h4>
                          <p className="text-xs text-gray-600">
                            Standardiserte dokumenter som brukes til innsamling av informasjon, 
                            registrering av data eller dokumentasjon av prosesser. Skjemaer sikrer konsistent datainnsamling.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowInfoTooltip(false)}
                        className="mt-3 w-full text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Lukk
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t("Søk i dokumenter...")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

                    {/* Filter Info */}
                    {(selectedCategory || selectedChapter) && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-blue-900">
                              {t("Filtrert på")}:
                            </span>
                            {selectedCategory && (
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                                {t("Kategori")}: {selectedCategory}
                              </span>
                            )}
                            {selectedChapter && (
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                                {t("Kapittel")}: {selectedChapter}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setSelectedCategory(null);
                              setSelectedChapter(null);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {t("Fjern filter")}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Documents List */}
                    <div className="bg-white shadow sm:rounded-lg">
                      <div className="px-4 py-5 sm:p-6">
                        {loading ? (
                          <ResourceLoading size="sm" />
                        ) : error ? (
                          <ResourceError 
                            errors={[t("Kunne ikke laste dokumenter")]} 
                            subtitle={t("Det oppstod en feil ved lasting av dokumenter. Vennligst prøv å oppdatere siden eller kontakt support hvis problemet vedvarer.")}
                          />
                        ) : documents.length === 0 ? (
                          <ResourceEmpty 
                            message={
                              selectedDiscipline && selectedDiscipline !== "" && selectedDiscipline !== null
                                ? selectedChapter 
                                  ? t(`Ingen dokumenter funnet for kapittel ${selectedChapter}`)
                                  : selectedCategory
                                    ? t("Ingen dokumenter funnet for valgt kategori")
                                    : t("Ingen dokumenter funnet for valgt fag/bransje")
                                : selectedChapter 
                                  ? t(`Ingen dokumenter funnet for kapittel ${selectedChapter}`)
                                  : t("Ingen dokumenter funnet")
                            } 
                          />
                        ) : (
                          <>
                            {/* Selection controls */}
                            <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3">
                              <div className="flex items-center space-x-4">
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={
                                      documents.length > 0 &&
                                      documents.filter(doc => {
                                        const hasApprovedVersion = doc.has_approved_version === true || doc.status === 'approved';
                                        return hasApprovedVersion;
                                      }).length > 0 &&
                                      documents.filter(doc => {
                                        const hasApprovedVersion = doc.has_approved_version === true || doc.status === 'approved';
                                        return hasApprovedVersion;
                                      }).every(doc => selectedDocumentsForPrint.has(doc.id))
                                    }
                                    onChange={handleSelectAllDocuments}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">
                                    {t("Velg alle")}
                                  </span>
                                </label>
                                {selectedDocumentsForPrint.size > 0 && (
                                  <span className="text-sm text-gray-500">
                                    {selectedDocumentsForPrint.size} {t("valgt")}
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => handlePrintMultiple()}
                                disabled={isPrintingBook}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isPrintingBook ? (
                                  <>
                                    <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t("Genererer...")}
                                  </>
                                ) : (
                                  <>
                                    <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                                    {t("Skriv ut dokument-bok")}
                                  </>
                                )}
                              </button>
                            </div>
                            
                            <ul className="divide-y divide-gray-200">
                              {documents.map((document) => {
                                // Check if document has approved version
                                // has_approved_version can be undefined if not set by API, so we check status as fallback
                                // Only documents with approved version can be printed or selected, even for admin
                                const hasApprovedVersion = document.has_approved_version === true || document.status === 'approved';
                                const canPrint = hasApprovedVersion; // No admin exception
                                const isSelected = selectedDocumentsForPrint.has(document.id);
                                
                                return (
                                <li key={document.id} className={`px-6 py-4 hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                                      <div className="flex-shrink-0">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => handleToggleDocumentSelection(document.id)}
                                          disabled={!canPrint}
                                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                          title={!canPrint ? t("Kun godkjente dokumenter kan velges") : t("Velg dokument")}
                                        />
                                      </div>
                                      <div className="flex-shrink-0">
                                        <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                                      </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center space-x-2">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                          {document.title}
                                        </p>
                                        {document.status === "approved" && (
                                          <CheckCircleIcon className="h-4 w-4 text-green-500" title="Godkjent" />
                                        )}
                                        {document.status === "pending_approval" && (
                                          <ClockIcon className="h-4 w-4 text-yellow-500" title="Venter på godkjenning" />
                                        )}
                                      </div>
                                      {document.description && (
                                        <p className="text-sm text-gray-500 truncate mt-1">
                                          {document.description}
                                        </p>
                                      )}
                                      <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                                        <span>{t("Versjon")} {document.current_version}</span>
                                        {document.document_type_display && (
                                          <span>{document.document_type_display}</span>
                                        )}
                                        {document.chapter &&
                                          (document.chapter.number != null || document.chapter.title) && (
                                          <span className="font-mono">
                                            {document.chapter.number != null ? `${document.chapter.number} - ` : ""}
                                            {document.chapter.title ?? ""}
                                          </span>
                                        )}
                                        {document.category_display && (
                                          <span>{document.category_display}</span>
                                        )}
                                        {document.updated_at && (
                                          <span>
                                            {t("Oppdatert")}: {new Date(document.updated_at).toLocaleDateString()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Link
                                      to={`/dashboard/hse-ks/documents/${document.id}`}
                                      className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                      <EyeIcon className="h-4 w-4 mr-1" />
                                      {t("Vis")}
                                    </Link>
                                    <button
                                      onClick={() => handlePrint(document.id)}
                                      disabled={!canPrint || printingDocumentId === document.id}
                                      className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                      title={
                                        !canPrint
                                          ? t("Kun dokumenter med godkjent versjon kan skrives ut")
                                          : printingDocumentId === document.id
                                          ? t("Genererer PDF...")
                                          : t("Skriv ut")
                                      }
                                    >
                                      {printingDocumentId === document.id ? (
                                        <>
                                          <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                          </svg>
                                          {t("Genererer...")}
                                        </>
                                      ) : (
                                        <>
                                          <PrinterIcon className="h-4 w-4 mr-1" />
                                          {t("Skriv ut")}
                                        </>
                                      )}
                                    </button>
                                    <RoleOnly role="hse_ks_documents" action="update">
                                      <Link
                                        to={`/dashboard/hse-ks/documents/${document.id}/edit`}
                                        className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                                      >
                                        <PencilIcon className="h-4 w-4 mr-1" />
                                        {t("Rediger")}
                                      </Link>
                                    </RoleOnly>
                                    {canApprove && document.status === "pending_approval" && (
                                      <Link
                                        to={`/dashboard/hse-ks/documents/${document.id}/approve`}
                                        className="inline-flex items-center px-2.5 py-1.5 border border-green-300 shadow-sm text-xs font-medium rounded text-green-700 bg-white hover:bg-green-50"
                                      >
                                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                                        {t("Godkjenn")}
                                      </Link>
                                    )}
                                  </div>
                                </div>
                              </li>
                              );
                            })}
                          </ul>
                          </>
                        )}
                      </div>
                    </div>
          </div>
        </div>
      </div>
    </DashboardHeader>
  );
};

